import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { UsersPage } from '@/pages/UsersPage';
import { ModerationPage } from '@/pages/ModerationPage';
import { SubscriptionsPage } from '@/pages/SubscriptionsPage';
import ProCouponsPage from '@/pages/ProCouponsPage';
import { SubscriptionsConfigPage } from '@/pages/SubscriptionsConfigPage';
import { AnalyticsPage } from '@/pages/AnalyticsPage';
import { SeoAuditPage } from '@/pages/SeoAuditPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { AppVersionsPage } from '@/pages/AppVersionsPage';
import { FeatureFlagsPage } from '@/pages/FeatureFlagsPage';
import { AIVideoConfigPage } from '@/pages/AIVideoConfigPage';
import { GeneralConfigPage } from '@/pages/GeneralConfigPage';
import { FeedAlgorithmConfigPage } from '@/pages/FeedAlgorithmConfigPage';
import { FishingNewsSourcesPage } from '@/pages/FishingNewsSourcesPage';
import { ToolsPage } from '@/pages/ToolsPage';
import { FunctionsStatusPage } from '@/pages/FunctionsStatusPage';
import { StoreIndexRedirect, StoreSectionLayout } from '@/pages/store/StorePage';
import { StoreOrdersPage } from '@/pages/store/orders/StoreOrdersPage';
import { StoreOrderDetailPage } from '@/pages/store/orders/StoreOrderDetailPage';
import { SupplierIndexPage } from '@/pages/store/suppliers/SupplierIndexPage';
import { SupplierDetailPage } from '@/pages/store/suppliers/SupplierDetailPage';
import { TemplateHelpPage } from '@/pages/store/help/TemplateHelpPage';
import { FichaHelpPage } from '@/pages/store/help/FichaHelpPage';
import { StoreProductsPage } from '@/pages/store/products/StoreProductsPage';
import { StoreMerchDetailPage } from '@/pages/store/products/StoreMerchDetailPage';
import { MerchStockPage } from '@/pages/store/products/MerchStockPage';
import { CatalogFichaEditorPage } from '@/pages/store/fichas/CatalogFichaEditorPage';
import { CatalogFichasImportPage } from '@/pages/store/fichas/CatalogFichasImportPage';
import { PartnersPanel } from '@/pages/store/propios/PartnersPanel';
import { SegundaVidaPanel } from '@/pages/store/segunda-vida/SegundaVidaPanel';
import { MaterialsPage } from '@/pages/store/MaterialsPage';
import { StoreShippingPage } from '@/pages/store/shipping/StoreShippingPage';
import { StoreSettingsBrandsPage } from '@/pages/store/settings/StoreSettingsBrandsPage';
import { StorePaymentsPage } from '@/pages/store/settings/StorePaymentsPage';
import { fichasQueueRedirect, ownPageRedirect } from '@/constants/store-nav';
import { AcademyPage } from '@/pages/academy/AcademyPage';
import { EventsPage } from '@/pages/events/EventsPage';
import { JornadasLandingPage } from '@/pages/jornadas/JornadasLandingPage';
import { ClubsPage } from '@/pages/clubs/ClubsPage';
import { ClubDetailPage } from '@/pages/clubs/ClubDetailPage';
import { BlogPage } from '@/pages/blog/BlogPage';
import { BlogPostEditorPage } from '@/pages/blog/BlogPostEditorPage';
import { BlogPostsImportPage } from '@/pages/blog/BlogPostsImportPage';
import { SpeciesPage } from '@/pages/species/SpeciesPage';
import { SpeciesDetailPage } from '@/pages/species/SpeciesDetailPage';
import { SpeciesCromosAlbumPage } from '@/pages/species/SpeciesCromosAlbumPage';
import { CatchFormCatalogPage } from '@/pages/catch-catalog/CatchFormCatalogPage';
import { CapturesPage } from '@/pages/captures/CapturesPage';
import { UserDetailPage } from '@/pages/UserDetailPage';
import { CampaignPopupsPage } from '@/pages/CampaignPopupsPage';
import { PescadilloPostsPage } from '@/pages/PescadilloPostsPage';
import { MaintenancePage } from '@/pages/MaintenancePage';
import { CofresPage } from '@/pages/cofres/CofresPage';
import { AppRatingFeedbackPage } from '@/pages/AppRatingFeedbackPage';
import { ChatPescadilloConfigPage } from '@/pages/ChatPescadilloConfigPage';
import { InvoicesPage } from '@/pages/billing/InvoicesPage';
import { OperationsResultPage } from '@/pages/operations/ResultPage';
import { ReceivedInvoicesPage } from '@/pages/operations/ReceivedPage';
import { DisbursementsPage } from '@/pages/operations/DisbursementsPage';
import { LicensesPage } from '@/pages/licenses/LicensesPage';
import { GestionesPage } from '@/pages/gestiones/GestionesPage';
import { BrandsPage } from '@/pages/brands/BrandsPage';
import { BrandDetailPage } from '@/pages/brands/BrandDetailPage';
import { OpsEmailNotificationsPage } from '@/pages/OpsEmailNotificationsPage';
import { CommunicationsPage } from '@/pages/communications/CommunicationsPage';
import type { ReactNode } from 'react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// ─── Protected Route ────────────────────────────────────────────────────────

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isLoading, isAdmin, logout } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Verificando acceso...</p>
          <button
            type="button"
            onClick={logout}
            className="mt-2 text-xs text-muted-foreground underline hover:text-foreground"
          >
            Si tarda demasiado, cerrar sesión
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🚫</span>
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Acceso Denegado</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Tu cuenta no tiene permisos de administrador.
            Contacta con el equipo de AiPeces.
          </p>
          <button
            onClick={logout}
            className="px-4 py-2 rounded-lg bg-muted text-foreground text-sm font-medium hover:bg-muted/80 transition-colors"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// ─── OAuth Callback Route ───────────────────────────────────────────────────
// Google redirects to http://localhost:3000/?code=xxx
// Without this route, the catch-all "*" would redirect to /dashboard,
// which ProtectedRoute would redirect to /login, LOSING the ?code= parameter.

function OAuthCallbackRoute() {
  const { user, isAdmin, isLoading } = useAuth();
  const hasCode = new URLSearchParams(window.location.search).has('code');

  // If there's a code in the URL, show loading while AuthProvider processes it
  if (hasCode || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Autenticando...</p>
        </div>
      </div>
    );
  }

  // Already logged in → go to dashboard
  if (user && isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Not logged in and no code → go to login
  return <Navigate to="/login" replace />;
}

function CatalogFichaRedirect() {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`/store/products/${id ?? ''}`} replace state={location.state} />;
}

function FichasQueueRedirect() {
  const [params] = useSearchParams();
  return <Navigate to={fichasQueueRedirect(params)} replace />;
}

function FichasImportRedirect() {
  return <Navigate to="/store/products/import" replace />;
}

function OwnRedirect() {
  const [params] = useSearchParams();
  return <Navigate to={ownPageRedirect(params)} replace />;
}

function CatalogRedirect() {
  const [params] = useSearchParams();
  const supplier = params.get('supplier')?.trim();
  if (supplier) {
    return <Navigate to={`/store/suppliers/${encodeURIComponent(supplier)}?sub=conexion`} replace />;
  }
  const q = params.get('q')?.trim();
  return <Navigate to={q ? `/store/products?q=${encodeURIComponent(q)}` : '/store/products'} replace />;
}

// ─── App ────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <Routes>
            {/* OAuth callback — must be BEFORE catch-all */}
            <Route path="/" element={<OAuthCallbackRoute />} />

            {/* Public */}
            <Route path="/login" element={<PublicRoute />} />

            {/* Protected Admin Routes */}
            <Route
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/seo" element={<SeoAuditPage />} />
              <Route path="/store" element={<StoreSectionLayout />}>
                <Route index element={<StoreIndexRedirect />} />
                <Route path="orders" element={<StoreOrdersPage />} />
                <Route path="orders/:orderId" element={<StoreOrderDetailPage />} />
                <Route path="products/stock" element={<MerchStockPage />} />
                <Route path="products/import" element={<CatalogFichasImportPage />} />
                <Route path="products/merch/:merchId" element={<StoreMerchDetailPage />} />
                <Route path="products/:productId" element={<CatalogFichaEditorPage />} />
                <Route path="products" element={<StoreProductsPage />} />
                <Route path="ayuda/plantilla" element={<TemplateHelpPage />} />
                <Route path="ayuda/fichas" element={<FichaHelpPage />} />
                <Route path="suppliers/:slug" element={<SupplierDetailPage />} />
                <Route path="suppliers" element={<SupplierIndexPage />} />
                <Route path="catalog" element={<CatalogRedirect />} />
                <Route path="fichas/import" element={<FichasImportRedirect />} />
                <Route path="fichas/:id" element={<CatalogFichaRedirect />} />
                <Route path="fichas" element={<FichasQueueRedirect />} />
                <Route path="own" element={<OwnRedirect />} />
                <Route path="partners" element={<PartnersPanel />} />
                <Route path="second-life" element={<SegundaVidaPanel />} />
                <Route path="affiliates" element={<MaterialsPage />} />
                <Route path="settings/shipping" element={<StoreShippingPage />} />
                <Route path="settings/brands" element={<StoreSettingsBrandsPage />} />
                <Route path="settings/payments" element={<StorePaymentsPage />} />
              </Route>
              <Route path="/licenses" element={<LicensesPage />} />
              <Route path="/gestiones" element={<GestionesPage />} />
              <Route path="/brands" element={<BrandsPage />} />
              <Route path="/brands/:id" element={<BrandDetailPage />} />
              <Route path="/academy" element={<AcademyPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/jornadas" element={<JornadasLandingPage />} />
              <Route path="/clubs" element={<ClubsPage />} />
              <Route path="/clubs/:id" element={<ClubDetailPage />} />
              <Route path="/blog/posts/import" element={<BlogPostsImportPage />} />
              <Route path="/blog/posts/new" element={<BlogPostEditorPage />} />
              <Route path="/blog/posts/:id" element={<BlogPostEditorPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/species/cromos-album" element={<SpeciesCromosAlbumPage />} />
              <Route path="/species" element={<SpeciesPage />} />
              <Route path="/species/:id" element={<SpeciesDetailPage />} />
              <Route path="/catch-catalog" element={<CatchFormCatalogPage />} />
              <Route path="/captures" element={<CapturesPage />} />
              <Route path="/moderation" element={<ModerationPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/communications" element={<CommunicationsPage />} />
              <Route path="/campaigns" element={<CampaignPopupsPage />} />
              <Route path="/pescadillo-posts" element={<PescadilloPostsPage />} />
              <Route path="/subscriptions" element={<SubscriptionsPage />} />
              <Route path="/operations" element={<Navigate to="/operations/resultado" replace />} />
              <Route path="/operations/resultado" element={<OperationsResultPage />} />
              <Route path="/operations/emitidas" element={<InvoicesPage />} />
              <Route path="/operations/recibidas" element={<ReceivedInvoicesPage />} />
              <Route path="/operations/suplidos" element={<DisbursementsPage />} />
              <Route path="/billing" element={<Navigate to="/operations/emitidas" replace />} />
              <Route path="/pro-coupons" element={<ProCouponsPage />} />
              <Route path="/settings/app" element={<AppVersionsPage />} />
              <Route path="/settings/maintenance" element={<MaintenancePage />} />
              <Route path="/settings/features" element={<FeatureFlagsPage />} />
              <Route path="/settings/subscriptions-config" element={<SubscriptionsConfigPage />} />
              <Route path="/settings/ai-video" element={<AIVideoConfigPage />} />
              <Route path="/settings/chat-pescadillo" element={<ChatPescadilloConfigPage />} />
              <Route path="/settings/general" element={<GeneralConfigPage />} />
              <Route path="/settings/ops-notifications" element={<OpsEmailNotificationsPage />} />
              <Route path="/cofres" element={<CofresPage />} />
              <Route path="/settings/capture-chest" element={<Navigate to="/cofres" replace />} />
              <Route path="/settings/merch-rewards" element={<Navigate to="/cofres" replace />} />
              <Route path="/settings/app-rating-feedback" element={<AppRatingFeedbackPage />} />
              <Route path="/settings/feed-algorithm" element={<FeedAlgorithmConfigPage />} />
              <Route path="/settings/fishing-news" element={<FishingNewsSourcesPage />} />
              <Route path="/settings/tools" element={<ToolsPage />} />
              <Route path="/settings/functions-status" element={<FunctionsStatusPage />} />
              <Route path="/users/:userId" element={<UserDetailPage />} />
            </Route>

            {/* Default redirect */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function PublicRoute() {
  const { user, isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user && isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LoginPage />;
}
